import { FC, useState } from 'react';

import type { TemplateSignatoryTypeEntity } from '../table/row/template-row.types';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { StorageDocumentLink } from '../../../../../components/storage-document-link/index';
import { SummaryItem } from '../../../../../components/summary-list/index';
import { useLocale } from '../../../../../hooks/index';
import { getS3FileOriginalName } from '../../../../../utils/get-s3file-original-name.util';
import { getTemplateSignatoryBaseDisplayLabel } from '../../constants/template-signatory-type.constant';
import {
  categoryLabelKey,
  expirationPeriodLabelKey,
  SigningMode,
  signingModeLabelKey,
  subCategoryLabelKey,
} from '../../enums/template.const';
import { useTemplateById } from '../../hooks/use-template-by-id.hook';
import { TemplateFormStore } from '../../store/template-form.store';
import { getFieldMappingDisplayLabel } from '../../utils/field-mapping-display-label.util';
import { ReviewTemplateModal } from './map-fields-modal/review-template-modal';

export const ViewTemplate: FC = () => {
  const { messagesLocale, messagesLocaleFn } = useLocale();
  const templateId = TemplateFormStore(({ templateId }) => templateId);
  const toggleState = TemplateFormStore(({ toggleState }) => toggleState);
  const { isError, isPending, template } = useTemplateById(templateId);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const handleClose = () => toggleState(false);

  const signatoriesLabel =
    Array.from(
      new Set(
        (template?.signatoryTypes ?? []).map((s: TemplateSignatoryTypeEntity) =>
          getTemplateSignatoryBaseDisplayLabel(s.stakeholderType, messagesLocale),
        ),
      ),
    ).join(', ') || '-';

  const signatoriesOrderLines = Array.from(
    new Set(
      (template?.signatoryTypes ?? []).map((s: TemplateSignatoryTypeEntity) =>
        getTemplateSignatoryBaseDisplayLabel(s.stakeholderType, messagesLocale),
      ),
    ),
  ).map((label, i: number) => `${i + 1}. ${label}`);

  const fieldMappings = template?.fieldMapping
    ? Object.entries(template.fieldMapping).map(([placeholder, field]) => ({ field, placeholder }))
    : [];

  if (!templateId) {
    return null;
  }

  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
        <span className="text-sm text-fireside-600">
          <AppFormattedMessage id="UNEXPECTED_ERROR_OCCURRED" />
        </span>
        <Button
          className="bg-brand-700 px-6 py-2 text-sm font-medium text-white"
          onClick={handleClose}
          styleType="DEFAULT_ROUNDED"
          type="button"
        >
          <AppFormattedMessage id="CLOSE" />
        </Button>
      </div>
    );
  }

  if (isPending || !template) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
        <span className="text-sm text-gray-500">
          <AppFormattedMessage id="LOADING" />
        </span>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-100">
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 py-4">
        <section className="flex flex-col gap-2 rounded-lg bg-gray-50 pt-3">
          <span className="px-4 text-sm font-semibold text-gray-700">
            <AppFormattedMessage id="DOCUMENT" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white px-4">
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="DOCUMENT_NAME" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={template.name}
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="CATEGORY" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={<AppFormattedMessage id={categoryLabelKey[template.category]} />}
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="SUB_CATEGORY" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={<AppFormattedMessage id={subCategoryLabelKey[template.subCategory]} />}
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="EQUITY_PLAN" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={template.equityPlan?.name ?? '-'}
            />
            {template.expirationPeriod && (
              <SummaryItem
                className="py-3"
                title={<AppFormattedMessage id="EXPIRATION_PERIOD" />}
                titleClassName="w-fit"
                titleWrapperClassName="w-[120px]"
                value={
                  <AppFormattedMessage id={expirationPeriodLabelKey[template.expirationPeriod]} />
                }
              />
            )}
          </div>
        </section>

        <div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white px-4">
          <SummaryItem
            className="py-3"
            title={<AppFormattedMessage id="SIGNATORIES" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={signatoriesLabel}
          />
          <SummaryItem
            className="py-3"
            title={<AppFormattedMessage id="SIGNING_MODE" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={
              messagesLocale[
                signingModeLabelKey[
                  template.isSignOrderMatters ? SigningMode.SET_ORDER : SigningMode.ALL_AT_ONCE
                ]
              ] ?? '-'
            }
          />
          {template.isSignOrderMatters && signatoriesOrderLines.length > 0 && (
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="SIGNATORIES_ORDER" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={
                <ul className="list-none pl-0 text-left">
                  {signatoriesOrderLines.map((line) => (
                    <li className="text-xs text-gray-700" key={line}>
                      {line}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
        </div>

        <div className="flex flex-col rounded-lg border border-gray-100 bg-white px-4">
          <SummaryItem
            className="pt-3"
            title={<AppFormattedMessage id="DOCUMENTS_LABEL" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={
              <StorageDocumentLink
                className="block h-auto w-full max-w-full cursor-pointer truncate p-0 text-left text-xs font-[450] text-brand-700 underline"
                label={template.documentName ?? getS3FileOriginalName(template.fileLink) ?? ''}
                link={template.fileLink}
              />
            }
          />
          <SummaryItem
            className="pb-3"
            title={<AppFormattedMessage id="MAPPED_FIELDS" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={String(fieldMappings.length)}
          />
        </div>

        <section className="flex flex-col rounded-lg bg-gray-50">
          <div className="flex h-9 items-center justify-between rounded-t-lg bg-gray-50 px-4">
            <span className="text-sm font-semibold text-gray-700">
              <AppFormattedMessage id="FIELD_MAPPINGS" />
            </span>
            <button
              className="text-xs font-medium text-brand-700 underline hover:text-brand-800"
              onClick={() => setIsReviewModalOpen(true)}
              type="button"
            >
              <AppFormattedMessage id="REVIEW_TEMPLATE" />
            </button>
          </div>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white px-4">
            {fieldMappings.length === 0 ? (
              <div className="py-3 text-xs font-[450] text-gray-500">—</div>
            ) : (
              fieldMappings.map((mapping) => (
                <div className="flex gap-3 py-3" key={mapping.placeholder}>
                  <span className="w-[140px] flex-shrink-0 truncate text-xs font-[450] text-gray-500">
                    {`{{${mapping.placeholder}}}`}
                  </span>
                  <span className="text-xs font-[450] text-gray-700 [overflow-wrap:anywhere]">
                    {getFieldMappingDisplayLabel({
                      messagesLocale,
                      messagesLocaleFn,
                      storedValue: mapping.field,
                    })}
                  </span>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      <div className="flex justify-end px-6 py-4">
        <Button
          className="bg-brand-700 px-6 py-2 text-sm font-medium text-white"
          onClick={handleClose}
          styleType="DEFAULT_ROUNDED"
          type="button"
        >
          <AppFormattedMessage id="CLOSE" />
        </Button>
      </div>

      {template && (
        <ReviewTemplateModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          template={template}
        />
      )}
    </div>
  );
};
